import { IFramework, IWebServiceManager, HttpMethod, IUserContext } from "@truckdown/framework";
import { IServiceTransactionManager } from "./IServiceTransactionManager";
import { AddTransactionModel } from "./Models/AddTransactionModel";
import { ArrayResultModel } from "./Models/ArrayResultModel";
import { GetTransactionsModel } from "./Models/GetTransactionsModel";
import { ResultModel } from "./Models/ResultModel";
import { ServiceTransaction } from "./Models/ServiceTransaction";

interface IResponse {
    ok: boolean,
    message: string,
    data: number
}

class ServiceTransactionManager implements IServiceTransactionManager {
    constructor(framework: IFramework) {
        this.manager = framework.getRequiredService<IWebServiceManager>('IWebServiceManager');
    }

    readonly manager: IWebServiceManager;
    private readonly basePath: string = '/locations';

    add(model: AddTransactionModel): Promise<ServiceTransaction | null> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: this.basePath + '/add-transaction',
            method: HttpMethod.PUT
        }, model);

        return this.manager
            .send(req)
            .then((res) => {
                if (res.ok) {
                    return res.json()
                        .then((j) => {
                            return j as ServiceTransaction;
                        });
                }

                if (res.status == 404) {
                    return null;
                }

                console.log("Error adding transaction ", res);
                return null;
            });
    }

    complete(transactionId: string): Promise<ServiceTransaction | null> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: this.basePath + '/complete-transaction/' + encodeURIComponent(transactionId),
            method: HttpMethod.PUT
        });

        return this.manager
            .send(req)
            .then((res) => {
                if (res.ok) {
                    return res.json()
                        .then((j) => {
                            return j as ServiceTransaction;
                        });
                }

                if (res.status == 404) {
                    return null;
                }

                console.log("Error completing transaction ", res);
                return null;
            });
    }

    open(transactionId: string): Promise<ServiceTransaction | null> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: this.basePath + '/open-transaction/' + encodeURIComponent(transactionId),
            method: HttpMethod.PUT
        });

        return this.manager
            .send(req)
            .then((res) => {
                if (res.ok) {
                    return res.json()
                        .then((j) => {
                            return j as ServiceTransaction;
                        });
                }

                if (res.status == 404) {
                    return null;
                }

                console.log("Error opening transaction ", res);
                return null;
            });
    }

    async getTransactionsCount(model?: GetTransactionsModel): Promise<number> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: this.basePath + '/get-transactions-count' + this.getQueryString(model),
            method: HttpMethod.GET
        });

        var res = await this.manager
            .get<IResponse>(req);

        return res.data;
    }

    getTransactions(model?: GetTransactionsModel): Promise<ArrayResultModel<ServiceTransaction>> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: this.basePath + '/get-transactions' + this.getQueryString(model),
            method: HttpMethod.GET
        });

        return this.manager
            .get<ArrayResultModel<ServiceTransaction>>(req);
    }

    private getQueryString(model?: GetTransactionsModel): string {
        if (!model) {
            return '';
        }

        var vals: string[] = [];

        if (model.startDate) {
            vals.push('startDate=' + model.startDate.toISOString());
        }
        if (model.endDate) {
            vals.push('endDate=' + model.endDate.toISOString());
        }
        if (model.limit) {
            vals.push('limit=' + model.limit);
        }
        if (model.lastModified) {
            vals.push('lastModified=' + model.lastModified.toISOString());
        }
        if (model.lastId) {
            vals.push('lastId=' + encodeURIComponent(model.lastId));
        }
        if (model.showAll) {
            vals.push('showAll=' + (model.showAll ? 'true' : 'false'));
        }
        
        if (vals.length == 0) {
            return '';
        }

        return '?' + vals.join('&');
    }

    expireTransactions(): Promise<number> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: this.basePath + '/expire-transactions',
            method: HttpMethod.PUT
        });

        return this.manager
            .send(req)
            .then((res) => {
                if (res.ok) {
                    res.json()
                    .then((j) => {
                        return (j as ResultModel<number>).results;
                    });
                }

                if (res.status == 404) {
                    return 0;
                }

                console.log("Error expiring transactions", res);
                return 0;
            });
    }

}

export default (framework: IFramework) => {
    framework.registerTransient<IServiceTransactionManager>('IServiceTransactionManager', (frm) => {
        return new ServiceTransactionManager(frm);
    });
}
